import React, { Fragment, useEffect, useState } from "react";
import {
  Button,
  Card,
  Col,
  Form,
  Input,
  Row,
  Table,
  Typography,
  Upload,
  Tabs,
  Tag,
  Image,
} from "antd";
import { InboxOutlined } from "@ant-design/icons";
import { CSVLink } from "react-csv";
import { HexColorPicker } from "react-colorful";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";

import baseUrl from "../../utils/baseUrl";
import ViewBtn from "../Buttons/ViewBtn";
import ColVisibilityDropdown from "../Shared/ColVisibilityDropdown";
import { CsvLinkBtn } from "../UI/CsvLinkBtn";
import UserPrivateComponent from "../PrivateRoutes/UserPrivateComponent";
import {
  addEmploymentStatus,
  loadAllEmploymentStatus,
} from "../../redux/rtk/features/employemntStatus/employmentStatusSlice";

const { Title } = Typography;
const { Dragger } = Upload;
const { TabPane } = Tabs;

/**
 * ----------------------------
 * CustomTable Component
 * ----------------------------
 * Displays employment status list in a table with CSV download option.
 */
function CustomTable({ list, loading }) {
  const [columnsToShow, setColumnsToShow] = useState([]);

  const columns = [
    // {
    //   id: 1,
    //   title: "ID",
    //   dataIndex: "id",
    //   key: "id",
    // },
    {
      id: 2,
      title: "Icon",
      dataIndex: "image",
      key: "image",
      render: (image) =>
        image ? (
          <Image
            src={`${baseUrl}/files/${image}`}
            width={50}
            height={50}
            style={{ objectFit: "cover", borderRadius: "40%" }}
            preview={{
              mask: <span style={{ fontSize: 13 }}>View Image</span>,
            }}
          />
        ) : (
          <Tag color="default">N/A</Tag>
        ),
    },
    {
      id: 3,
      title: "Name",
      dataIndex: "name",
      key: "name",
    },
    {
      id: 4,
      title: "Color Code",
      dataIndex: "colourValue",
      key: "colourValue",
      render: (colourValue) => (
        <div className="flex">
          <div
            className="rounded border border-gray-200"
            style={{
              marginRight: "10px",
              width: "20px",
              height: "20px",
              backgroundColor: colourValue,
            }}
          ></div>
          {colourValue}
        </div>
      ),
    },
    {
      id: 5,
      title: "Description",
      dataIndex: "description",
      key: "description",
    },
    {
      id: 6,
      title: "Action",
      dataIndex: "id",
      key: "action",
      render: (id) => <ViewBtn path={`/admin/employment-status/${id}/`} />,
    },
  ];

  useEffect(() => {
    setColumnsToShow(columns);
  }, []);

  const columnsToShowHandler = (val) => {
    setColumnsToShow(val);
  };

  const addKeys = (arr) => arr.map((i) => ({ ...i, key: i.id }));

  return (
    <Card>
      <div className="text-center my-2 flex justify-between">
        <h5 className="department-list-title text-color-2 text-xl mb-2">
          Category List
        </h5>
        {list && (
          <div>
            <CsvLinkBtn>
              <CSVLink
                data={list}
                className="btn btn-dark btn-sm mb-1"
                filename="categories"
              >
                Download CSV
              </CSVLink>
            </CsvLinkBtn>
          </div>
        )}
      </div>

      {list && (
        <div style={{ marginBottom: "30px" }}>
          <ColVisibilityDropdown
            options={columns}
            columns={columns}
            columnsToShowHandler={columnsToShowHandler}
          />
        </div>
      )}

      <Table
        scroll={{ x: true }}
        loading={loading}
        columns={columnsToShow}
        dataSource={list ? addKeys(list) : []}
      />
    </Card>
  );
}

/**
 * ----------------------------
 * AddEmploymentStatus Component
 * ----------------------------
 * Allows adding a new employment category (multilingual).
 * Supports:
 * - English & Dutch translations
 * - Color picker
 * - Image upload
 */
const AddEmploymentStatus = ({ drawer }) => {
  const [loader, setLoader] = useState(false);
  const [color, setColor] = useState("#ffffff");
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [language, setLanguage] = useState("en"); // toggle between languages for table display

  const dispatch = useDispatch();
  const { list, loading } = useSelector((state) => state.employmentStatus);
  const [form] = Form.useForm();

useEffect(() => {
  dispatch(loadAllEmploymentStatus(language));
}, [dispatch, language]);

  /**
   * ----------------------------
   * Form Submit Handler
   * ----------------------------
   * Sends English and Dutch data to backend.
   */
  const onFinish = async (values) => {
    try {
      setLoader(true);

      const formData = new FormData();

      // Append image if selected
      if (values.image && values.image[0]?.originFileObj) {
        formData.append("image", values.image[0].originFileObj);
      }

      // Append multilingual fields
      formData.append("name_en", values.name_en?.trim());
      formData.append("description_en", values.description_en?.trim() || "");
      formData.append("name_nl", values.name_nl?.trim());
      formData.append("description_nl", values.description_nl?.trim() || "");
      formData.append("colourValue", color || "#ffffff");

      const resp = await dispatch(addEmploymentStatus(formData));

      if (resp.payload?.message === "success") {
        toast.success("Category added successfully!");
        form.resetFields();
        setColor("#ffffff");
        dispatch(loadAllEmploymentStatus());
      }
    } catch (error) {
      console.error("Error in onFinish:", error.message);
      toast.error("Failed to add category");
    } finally {
      setLoader(false);
    }
  };

  const onFinishFailed = () => {
    toast.warning("Please fill required fields correctly");
    setLoader(false);
  };

  /**
   * ----------------------------
   * Language Toggle Handler
   * ----------------------------
   * Controls which language name/description to show in table.
   */
  const toggleLanguage = () => {
    setLanguage((prev) => (prev === "en" ? "nl" : "en"));
  };

  // Filter list for current language display
  const translatedList = list?.map((item) => {
    const translation = item.translations?.find(
      (t) => t.languageCode === language
    );
    return {
      ...item,
      name: translation?.name || item.name,
      description: translation?.description || item.description,
    };
  });

  return (
    <Fragment>
      {/* Create Employment Status */}
      <UserPrivateComponent permission={"create-employmentStatus"}>
        <Row className="mr-top" justify={drawer ? "center" : "space-between"}>
          <Col
            xs={24}
            sm={24}
            md={24}
            lg={drawer ? 22 : 16}
            xl={drawer ? 22 : 12}
            className="column-design border rounded card-custom"
          >
            <Title level={4} className="m-2 mt-5 mb-5 text-center">
              Add Category (Multilingual)
            </Title>

            <Form
              form={form}
              style={{ marginBottom: "40px" }}
              name="employment-status-form"
              labelCol={{ span: 6 }}
              wrapperCol={{ span: 12 }}
              onFinish={onFinish}
              onFinishFailed={onFinishFailed}
              autoComplete="off"
            >
              {/* Language Tabs */}
              <Tabs defaultActiveKey="en">
                <TabPane tab="English" key="en">
                  <Form.Item
                    label="Name"
                    name="name_en"
                    rules={[
                      { required: true, message: "Please enter English name!" },
                    ]}
                  >
                    <Input placeholder="Electrician" />
                  </Form.Item>

                  <Form.Item label="Description" name="description_en">
                    <Input.TextArea placeholder="Description" />
                  </Form.Item>
                </TabPane>

                <TabPane tab="Dutch" key="nl">
                  <Form.Item
                    label="Naam"
                    name="name_nl"
                    rules={[
                      { required: true, message: "Please enter Dutch name!" },
                    ]}
                  >
                    <Input placeholder="Elektricien" />
                  </Form.Item>

                  <Form.Item label="Beschrijving" name="description_nl">
                    <Input.TextArea placeholder="Beschrijving" />
                  </Form.Item>
                </TabPane>
              </Tabs>

              {/* Color Picker */}
              <Form.Item label="Color Code" name="colourValue">
                <Input
                  placeholder="#00FF00"
                  value={color}
                  onChange={(e) => setColor(e.target.value)}
                  onClick={() => setShowColorPicker(true)}
                />
                {showColorPicker && (
                  <div className="flex justify-between mt-3 mb-3">
                    <HexColorPicker onChange={setColor} color={color} />
                    <Button
                      type="danger"
                      onClick={() => setShowColorPicker(false)}
                    >
                      Close
                    </Button>
                  </div>
                )}
              </Form.Item>

              {/* Image Upload */}
              <Form.Item
                label="Upload Image"
                name="image"
                valuePropName="fileList"
                getValueFromEvent={(e) =>
                  Array.isArray(e) ? e : e?.fileList
                }
              >
                <Dragger
                  name="file"
                  multiple={false}
                  accept="image/*"
                  beforeUpload={() => false}
                  maxCount={1}
                  style={{
                    padding: "12px",
                    borderRadius: "8px",
                    minHeight: 150,
                  }}
                >
                  <p className="ant-upload-drag-icon">
                    <InboxOutlined />
                  </p>
                  <p className="ant-upload-text">
                    Click or drag image to upload
                  </p>
                </Dragger>
              </Form.Item>

              {/* Submit Button */}
              <Form.Item
                wrapperCol={{
                  offset: 6,
                  span: 12,
                }}
              >
                <Button
                  type="primary"
                  size="large"
                  block
                  htmlType="submit"
                  loading={loader}
                >
                  Add Category
                </Button>
              </Form.Item>
            </Form>
          </Col>
        </Row>
      </UserPrivateComponent>

      <hr />

      {/* Employment Status List with Language Toggle */}
      <UserPrivateComponent permission={"readAll-employmentStatus"}>
        {!drawer && (
          <>
            <div className="flex justify-end mb-3">
              <Button onClick={toggleLanguage}>
                Switch to {language === "en" ? "Dutch" : "English"}
              </Button>
            </div>
            <CustomTable list={translatedList} loading={loading} />
          </>
        )}
      </UserPrivateComponent>
    </Fragment>
  );
};

export default AddEmploymentStatus;